// src/utils/aiBaseUrl.js
// Environment-aware AI API base URL selector for FroggerAI/FroggyAI

/**
 * Normalize URL to host-only (strip paths like /api)
 * Handles:
 * - Full URLs: https://api.feelingfroggy.llc/api → https://api.feelingfroggy.llc
 * - Hostnames: api.feelingfroggy.llc → https://api.feelingfroggy.llc
 * - With paths: api.feelingfroggy.llc/api → https://api.feelingfroggy.llc
 *
 * @param {string} url - URL or hostname to normalize
 * @returns {string} Normalized host-only URL
 */
function normalizeToHostOnly(url) {
  if (!url) return url;

  let normalized = url.trim();

  // If it doesn't start with http:// or https://, prepend https://
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    // Special case: localhost without protocol should be http
    if (normalized.startsWith('localhost')) {
      normalized = 'http://' + normalized;
    } else {
      normalized = 'https://' + normalized;
    }
  }

  try {
    // Use URL API to extract origin (strips pathname)
    const urlObj = new URL(normalized);
    return urlObj.origin; // Returns protocol + host, no path
  } catch (e) {
    console.warn('[getAiBaseUrl] Invalid URL, returning as-is:', normalized);
    // Fallback: strip /api or /api/ suffix manually
    return normalized.replace(/\/api\/?$/, '').replace(/\/+$/, '');
  }
}

/**
 * Get AI API base URL without hard-coded hostnames
 * Priority: REACT_APP_AI_API_URL env → hostname mapping → prod default
 * Always returns host-only URL (no /api path)
 *
 * @returns {string} Base URL for AI endpoints (e.g., https://api.feelingfroggy.llc)
 */
export function getAiBaseUrl() {
  // Highest priority: explicit override (CRA-safe)
  const explicit = process.env.REACT_APP_AI_API_URL;
  if (explicit) {
    const normalized = normalizeToHostOnly(explicit);
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[getAiBaseUrl] Using REACT_APP_AI_API_URL:', normalized);
    }
    return normalized;
  }

  // Hostname based mapping (works for beta/prod without extra env)
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (host.includes('localhost') || host.endsWith('.local')) {
    return 'http://localhost:8000';
  }
  if (host.includes('beta.') || host.includes('api-beta') || host.includes('beta-feelingfroggy')) {
    return 'https://api-beta.feelingfroggy.llc';
  }

  // Default: prod
  return 'https://api.feelingfroggy.llc';
}
